import {
  Box,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ErrorIcon from "@mui/icons-material/Error";
import React, { useEffect, useState } from "react";
import { COLORS } from "../../assets/theme";
import CustomButton from "../../components/common/CustomButton";
import AutoComplete from "../../components/common/AutoComplete";
import CustomSwitch from "../../components/common/CustomSwitch";
import FileUploadField from "../../components/FileUploadField";
import ImportForm from "../../components/ImportForm";
import ToastNotification from "../../components/ToastNotification";
import config from "../../config.json";
import {
  IExport,
  uploadServiceProviderCostsExcel,
  useServiceProviderImportedFilesQuery,
  ServiceProviderCostTypeEnum,
  SERVICE_PROVIDER_COST_TYPE_OPTIONS,
} from "../../services/ServiceProviderCostsService";
import {
  fetchServiceProviders,
  getServiceProviderNameOptions,
  getServiceProviderById,
} from "../../services/ServiceProviderService";
import { IField, Option, Params } from "../../types/table/interfaces.type";
import CustomTextField from "../../components/common/CustomTextField";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTable from "../../components/common/table/CustomTable";
import Actions from "../../components/common/Actions";
import {
  ExportAttachmentApi,
  ExportErrorApi,
  IErrors,
} from "../../services/ExportService";
import CustomDialog from "../../components/common/CustomDialog";
import ExportTable from "../../components/ExportTable";
import GetErrors from "../../components/GetErrors";
import { hasJwtPermission } from "../../services/ServicesHelpers";

const ImportServiceProviderCostPage: React.FC = () => {
  const [serviceProvider, setServiceProvider] = useState<string>("");
  const [costType, setCostType] = useState<ServiceProviderCostTypeEnum | "">("");
  const [displayType, setDisplayType] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [removeAll, setRemoveAll] = useState<boolean>(false);
  const [serviceProvidersOptions, setServiceProvidersOptions] = useState<Option[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [textFields, setTextFields] = useState({
    // rateCount: "1",
    mccmnc: "",
    mcc: "",
    mnc: "",
    rate: "Rate",
    effectiveDate: "",
    operatorName: "OperatorName",
    countryName: "CountryName",
  });

  const [appliedFilters] = useState<Params<IExport>>({
    usedBy: "SERVICE_PROVIDER_COST",
  });

  const queryClient = useQueryClient();

  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [selectedExport, setSelectedExport] = useState<IExport | null>(null);
  const [confirmNoDateOpen, setConfirmNoDateOpen] = useState<boolean>(false);

  const actions = Actions({
    onExportAttem: {
      handler: (row: any) => {
        ExportAttachmentApi(row.attachmentId, row.usedBy);
      },
      isDisabled: !hasJwtPermission("DOWNLOAD_SERVICE_PROVIDER_COST_FILE"),
    },

    onView: {
      handler: (row: any) => {
        setSelectedExport(row as IExport);
        setViewDialogOpen(true);
      },
      isDisabled: !hasJwtPermission("GET_ALL_SERVICE_PROVIDER_COST_IMPORT_ERRORS"),
    },
  });
  const fields: IField<IExport>[] = [
    {
      key: "serviceProviderId",
      header: "Service Provider",
      options: (t) => getServiceProviderNameOptions(t.serviceProviderId),
    },
    { key: "fileName", header: "File Name" },
    { key: "createdBy", header: "Created By" },
    { key: "createdDate", header: "Created Date" },
    {
      key: "status",
      header: "Status",
      transform: (row: IExport) => {
        switch (row.status?.toLowerCase()) {
          case "success":
            return (
              <Tooltip title="Full Import succeeded">
                <CheckCircleIcon sx={{ color: "green" }} />
              </Tooltip>
            );
          case "fail":
          case "failed":
            return (
              <Tooltip title="All entries failed">
                <CancelIcon sx={{ color: "red" }} />
              </Tooltip>
            );
          case "pending":
            return (
              <Tooltip title="Pending">
                <HourglassEmptyIcon sx={{ color: "orange" }} />
              </Tooltip>
            );
          case "errors":
            return (
              <Tooltip title="Errors occurred">
                <ErrorIcon sx={{ color: "darkorange" }} />
              </Tooltip>
            );
          default:
            return row.status;
        }
      },
    },
  ];

  const ErrorField: IField<IErrors>[] = [
    { key: "rowNumber", header: "Row Number" },
    { key: "errorMessage", header: "Error Message" },
    { key: "createdBy", header: "Created By" },
    { key: "createdDate", header: "Created Date" },
  ];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const loadServiceProviders = async () => {
      try {
        const response = await fetchServiceProviders({
          size: config.MAX_PAGE_SIZE,
        });
        const options = response.content.map((sp) => ({
          label: sp.name,
          value: sp.id,
        }));
        setServiceProvidersOptions(options);
      } catch (error: any) {
        ToastNotification.error("Error loading service providers. ", error);
        console.error("Error loading service providers. ", error);
      }
    };

    loadServiceProviders();
  }, []);

  const handleFileChange = (file: File | null) => {
    setFile(file);
  };

  const performUpload = async () => {
    try {
      setIsUploading(true);
      queryClient.invalidateQueries({ queryKey: ["serviceProviderImportedFiles"] });
      queryClient.refetchQueries({ queryKey: ["serviceProviderImportedFiles"] });
      await uploadServiceProviderCostsExcel(serviceProvider, file!, removeAll, textFields);
      ToastNotification.success("File uploaded successfully!");
    } catch (err: any) {
      ToastNotification.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!serviceProvider) {
      ToastNotification.error("Please select a service provider.");
      return;
    }

    if (!file) {
      ToastNotification.error("Please select an Excel File file.");
      return;
    }

    if (!textFields.effectiveDate.trim()) {
      setConfirmNoDateOpen(true);
      return;
    }

    await performUpload();
  };

  const formStyles = {
    padding: { xs: 2, sm: 5 },
    borderRadius: "12px",
    backgroundColor: "#fff",
  };

  const uploadButton = (
    <CustomButton
      size="small"
      variant="contained"
      label={isUploading ? "Uploading..." : "Upload"}
      textColor={COLORS.textPrimary}
      bgColor={COLORS.GOLD}
      disabled={isUploading || !hasJwtPermission("IMPORT_SERVICE_PROVIDER_COST")}
      onClick={handleUpload}
      sx={{ mt: 1, ml: "auto" }}
      loading={false}
    />
  );

  const additionalComponents = [
    <Box
      key="row-1"
      display="flex"
      alignItems={{ xs: "stretch", sm: "end" }}
      flexDirection={{ xs: "column", sm: "row" }}
      gap={{ xs: 2.5, sm: 2 }}
      sx={{ 
        marginTop: 2,
        px: { xs: 1, sm: 0 } // Add horizontal padding on mobile
      }}
    >
      <Box 
        flex={{ xs: "1", sm: "32%" }}
        sx={{ 
          width: "100%",
          minWidth: 0 // Prevent overflow
        }}
      >
        <AutoComplete
          label="Service Provider"
          value={serviceProvider}
          highlightLabelStar={true}
          sx={{ "& .MuiInputBase-root": { backgroundColor: COLORS.WhiteBackground } }}
          options={serviceProvidersOptions}
          placeholder="Select Service Provider"
          onChange={async (value) => {
            const selectedProviderId = value as string;
            setServiceProvider(selectedProviderId);

            if (selectedProviderId) {
              try {
                const provider = await getServiceProviderById(selectedProviderId);
                setCostType(provider.type);
                const typeLabel = SERVICE_PROVIDER_COST_TYPE_OPTIONS.find(
                  opt => opt.value === provider.type
                )?.label || provider.type;
                setDisplayType(typeLabel);
              } catch (error: any) {
                console.error("Error fetching service provider:", error);
                ToastNotification.error("Error loading service provider details");
                setCostType("");
                setDisplayType("");
              }
            } else {
              setCostType("");
              setDisplayType("");
            }
          }}
        />
      </Box>

      <Box 
        flex={{ xs: "1", sm: "32%" }}
        sx={{ 
          width: "100%",
          minWidth: 0 // Prevent overflow
        }}
      >
        <CustomTextField
          label="Type"
          value={displayType}
          bgColor={COLORS.WhiteBackground}
          fullWidth
          placeholder="Type"
          disabled={true}
          size="small"
        />
      </Box>

      <Box 
        flex={{ xs: "1", sm: "32%" }}
        sx={{ 
          width: "100%",
          minWidth: 0, // Prevent overflow
          mt: { xs: 1, sm: 0 } // Add top margin on mobile for better spacing
        }}
      >
        <FileUploadField
          title="File"
          placeholder="Add Excel file"
          accept=".xlsx,.xls,.csv"
          popupImageUrl="/ServiceProviderCost.png"
          popupImageAlt="Service Provider Cost Excel File"
          onFileChange={handleFileChange}
        />
      </Box>
    </Box>,
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
        Excel Header Fields
      </Typography>
    </Box>,
    <Grid
      container
      spacing={2}
      sx={{
        overflowX: "none",
        width: "100%",
      }}
      key="text-fields-row"
    >
      {!Boolean(textFields.mcc.trim()) && !Boolean(textFields.mnc.trim()) && (
        <Grid component={"div" as React.ElementType}>
          <CustomTextField
            label="MCCMNC Header"
            placeholder="MCCMNC"
            size="small"
            highlightLabelStar
            bgColor={COLORS.WhiteBackground}
            value={textFields.mccmnc}
            onChange={(e) =>
              setTextFields({ ...textFields, mccmnc: e.target.value })
            }
          />
        </Grid>
      )}

      {!Boolean(textFields.mccmnc.trim()) && (
        <Grid component={"div" as React.ElementType}>
          <CustomTextField
            label="MCC Header"
            placeholder="MCC"
            size="small"
            highlightLabelStar
            bgColor={COLORS.WhiteBackground}
            value={textFields.mcc}
            onChange={(e) =>
              setTextFields({ ...textFields, mcc: e.target.value })
            }
          />
        </Grid>
      )}

      {!Boolean(textFields.mccmnc.trim()) && (
        <Grid component={"div" as React.ElementType}>
          <CustomTextField
            label="MNC Header"
            placeholder="MNC"
            size="small"
            highlightLabelStar
            bgColor={COLORS.WhiteBackground}
            value={textFields.mnc}
            onChange={(e) =>
              setTextFields({ ...textFields, mnc: e.target.value })
            }
          />
        </Grid>
      )}

      <Grid component={"div" as React.ElementType}>
        <CustomTextField
          label="Rate Header"
          placeholder="Rate"
          size="small"
          highlightLabelStar
          bgColor={COLORS.WhiteBackground}
          value={textFields.rate}
          onChange={(e) =>
            setTextFields({ ...textFields, rate: e.target.value })
          }
        />
      </Grid>

      <Grid component={"div" as React.ElementType}>
        <CustomTextField
          label="Effective Date Header"
          placeholder="Effective Date"
          size="small"
          bgColor={COLORS.WhiteBackground}
          value={textFields.effectiveDate}
          onChange={(e) =>
            setTextFields({ ...textFields, effectiveDate: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType}>
        <CustomTextField
          label="Operator Name Header"
          placeholder="OperatorName"
          size="small"
          bgColor={COLORS.WhiteBackground}
          value={textFields.operatorName}
          onChange={(e) =>
            setTextFields({ ...textFields, operatorName: e.target.value })
          }
        />
      </Grid>
      <Grid component={"div" as React.ElementType}>
        <CustomTextField
          label="Country Header"
          placeholder="CountryName"
          size="small"
          bgColor={COLORS.WhiteBackground}
          value={textFields.countryName}
          onChange={(e) =>
            setTextFields({ ...textFields, countryName: e.target.value })
          }
        />
      </Grid>
    </Grid>,
    <Box
      key="row-2"
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems="center"
      gap={2}
      sx={{ marginTop: 2 }}
    >
      <Box flex={{ xs: "50%", sm: "48%" }}>
        <CustomSwitch
          label="Delete all rates not found in this sheet"
          checked={removeAll}
          onChange={(event) => setRemoveAll(event.target.checked)}
          activeText="ON"
          inactiveText="OFF"
          fontWeight="300"
          sx={{ mt: 3 }}
          fontSize="0.875rem"
        />
      </Box>
      <Box 
        flex={{ xs: "100%", sm: "48%" }}
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          mt: { xs: 2, sm: 1 }
        }}
      >
        <CustomButton
          size="medium"
          variant="contained"
          label={"Upload"}
          textColor={COLORS.textPrimary}
          bgColor={COLORS.GOLD}
          onClick={handleUpload}
          sx={{ 
            mt: 1, 
            ml: { xs: 0, sm: "auto" },
            width: { xs: "100%", sm: "auto" },
            minHeight: { xs: "48px", sm: "36px" },
            fontSize: { xs: "1rem", sm: "0.875rem" },
            fontWeight: { xs: "500", sm: "400" },
            padding: { xs: "12px 24px", sm: "8px 16px" }
          }}
          loading={false}
        />
      </Box>
    </Box>,
  ];


  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 3 },
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <CustomLabel
        text="Import Service Provider Cost"
        variant={isSmallScreen ? "h6" : "h5"}
        fontWeight={500}
        marginBottom={1}
        sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}
      />

      <ImportForm
        additionalComponents={additionalComponents}
        gap={3}
        layout="column"
        sx={formStyles}
      />
      <Box mt={{ xs: 2, sm: 3 }}>
        <CustomLabel
          text="Cost Sessions List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}
        />
      </Box>
      <Box
        mt={1}
        sx={{ width: "100%", overflowX: { xs: "auto", sm: "unset" } }}
      >
        <CustomTable<IExport>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useServiceProviderImportedFilesQuery}
          actions={actions}
          sortableFields={["status", "createdDate", "createdBy"]}
        />
      </Box>

      <CustomDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        title="View Error Details"
        mode="history"
        fields={[
          {
            type: "custom",
            component: (
              <GetErrors
                usedBy="SERVICE_PROVIDER_COST"
                fields={ErrorField}
                exportId={String(selectedExport)}
              />
            ),
          },
        ]}
        buttons={[{ label: "Close", onClick: () => setViewDialogOpen(false) }]}
      />

      <CustomDialog
        open={confirmNoDateOpen}
        onClose={() => setConfirmNoDateOpen(false)}
        title="Missing Effective Date Header"
        mode="confirm"
        fields={[
          {
            type: "custom",
            component: (
              <Typography variant="body2">
                The <strong>Effective Date Header</strong> field is empty. Proceeding will import
                the file without mapping an effective date column. Are you sure you want to
                continue?
              </Typography>
            ),
          },
        ]}
        buttons={[
          {
            label: "Proceed",
            onClick: async () => {
              setConfirmNoDateOpen(false);
              await performUpload();
            },
          },
        ]}
      />
    </Box>
  );
};

export default ImportServiceProviderCostPage;
